import {AfterViewInit, Component, OnInit, Renderer2, ViewChild} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {UtilsService} from "../shared/utils.service";
import {AppService} from "../app.service";
import {ApiRequestService} from "../shared/api-request.service";
import {MatDialog} from "@angular/material/dialog";
import {tap} from "rxjs/operators";
import {UserPublicProfileComponent} from "../shared/user-public-profile/user-public-profile.component";
import {ApiDataSource} from "../utils/api-data-source";
import {AssetTypeModel} from "../models/asset-type.model";
import {AssetTypesViewComponent} from "./asset-types-view/asset-types-view.component";
import {AssetTypesEditComponent} from "./asset-types-edit/asset-types-edit.component";
import {AssetTypesFilterComponent} from "./asset-types-filter/asset-types-filter.component";
import {saveAs} from "file-saver";
import moment from "moment";

@Component({
  selector: 'app-asset-types',
  templateUrl: './asset-types.component.html',
  styleUrls: ['./asset-types.component.scss']
})
export class AssetTypesComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new AssetTypesDataSource(this.app, this.api);

  displayedColumns = [
    'select',
    'id',
    'name',
    'description',
    'linked_assets_count',
    'created_by_user',
    'date_created',
    'buttons'
  ];

  on_parent_list: boolean = true;

  constructor(
    private utils: UtilsService,
    private app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    public renderer: Renderer2
  ) { }

  ngOnInit() {
    this.dataSource.getData(false);
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === "date_created_UTC") {
        this.dataSource.order_by = "date_created";
      } else {
        this.dataSource.order_by = this.sort.active;
      }


      this.dataSource.getData(false);
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Create a new Asset Type.
   *
   * @return {void}
   */
  onAdd(): void {
    this.onEdit(new AssetTypeModel());
  }

  /**
   * Create new or update existing Asset Types.
   *
   * @param {AssetTypeModel} assetType The asset type to be edited.
   * @return {void}
   */
  onEdit(assetType: AssetTypeModel): void {
    this.dialog
      .open(AssetTypesEditComponent, {
        width: '900px',
        data: {
          asset_type_id: assetType.id,
          on_parent_list: this.on_parent_list
        }
      })
      .afterClosed()
      .subscribe(() => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      });
  }

  /**
   * Display Asset Type data.
   *
   * @param {number} id - The unique identifier of the asset type.
   * @return {void}
   */
  onView(id: number): void {
    this.utils.showComponentDialog(
      AssetTypesViewComponent,
      id,
      { width: '700px' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Archives Asset Types and any associated Subtypes.
   *
   * @return {void}
   */
  onRemoveSelected(): void {
    this.utils.showModal(
      'Archive Asset Type(s)',
      `Are you sure you want to archive ${this.dataSource.selection.selected.length} asset type(s)? If there are any Subtypes then they will be archived as well.`,
      () => {
        this.remove(this.dataSource.selection.selected.join(','), 'Asset Type(s) have been archived.');
      }
    );
  }

  /**
   * Archives the specified Asset Type and any associated Subtypes.
   *
   * @param {AssetTypeModel} assetType - The asset type to be archived.
   * @return {void}
   */
  onRemove(assetType: AssetTypeModel): void {
    this.utils.showModal(
      'Archive Asset Type',
      `Are you sure you want to archive "${assetType.name}"? If there are any Subtypes then they will be archived as well.`,
      () => {
        this.remove(assetType.id, 'Asset Type has been archived.');
      }
    );
  }

  /**
   * Archives Asset Types with the specified IDs.
   *
   * @param {any} ids - The asset type IDs to be removed.
   * @param msg
   * @return {void}
   */
  private remove(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v2/asset-types/${ids}`)
      .then((response) => {
        // The backend returns a specific message when some types could not be archived
        // because they are linked to assets; prefer it over the generic message.
        this.utils.showToast(response?.message || msg);
        this.dataSource.selection.clear();
        this.dataSource.getData(false);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Restore Asset Type.
   */
  onRestore(id: number) {
    this.utils.showModal(
      'Restore Asset Type',
      'Are you sure you want to restore this asset type?',
      () => {
        this.restore(id, 'The Asset Type was restored.');
      }
    );
  }

  /**
   * Restore selected Asset Types.
   */
  onRestoreSelected() {
    this.utils.showModal(
      'Restore Selected Asset Types',
      'Are you sure you want to restore the selected asset types?',
      () => {
        this.restore(this.dataSource.selection.selected.join(','), 'The Asset Type(s) were restored.');
      }
    );
  }

  /**
   * Restore Asset Types with the specified IDs.
   *
   * @param {any} ids - The asset type IDs to be restored.
   * @param msg
   * @return {void}
   */
  private restore(ids: any, msg: string): void {
    this.api.makeRequest('put', `v2/asset-types/${ids}/restore`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Show component dialog for filtering Asset Types.
   *
   * @return {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(AssetTypesFilterComponent, {
        filter_archived: this.dataSource.archived,
        filter_on_parent_list: this.on_parent_list
      }, {
        width: '500px'
      },
      (results: any): void => {
        // Terminate early if it is undefined or an empty string.
        if ( typeof results === 'undefined' || results === '' ) {
          return;
        }

        // Apply the filters.
        this.dataSource.archived = results.archived ?? this.dataSource.archived;
        this.dataSource.getData();
      }
    );
  }

  /**
   * Exports the list of Asset Types into the specified format and sends a download request to the browser.
   *
   * @param type csv, xls, xlsx, pdf.
   */
  onExportSelected(type: string = 'csv'): void {
    this.dataSource.makeDownloadRequest(`v2/asset-types/export/${type}` + (this.dataSource.selection.selected.length ? '/' + this.dataSource.selection.selected.join(',') : ''), {
      archived: this.dataSource.archived
    })
      .then((response) => {
        saveAs(response, `Asset Types - ${moment().toString()}.${type}`);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Open the User's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

}

export class AssetTypesDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';
  archived = "false";

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/asset-types', resetOffset, {
      archived: this.archived
    });
  }
}
